import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";
import SiteHead from "@/components/SiteHead";
import {
  ArrowLeft, Store, CheckCircle2, Loader2, ShieldCheck, Lock, Tag, XCircle, MailCheck, Sparkles,
  Zap, IndianRupee, Heart, Eye, EyeOff, Star,
} from "lucide-react";
import { useMerchantAuth } from "@/lib/auth";
import api, { extractErr, mediaUrl } from "@/lib/api";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const LEFT_PHOTO = "https://images.unsplash.com/photo-1780504863007-44f229d4d33f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzdG9yZSUyMG93bmVyJTIwc21pbGluZyUyMGNvbW1lcmNlJTIwYXBwYXJlbCUyMGNyYWZ0fGVufDB8fHx8MTc4NjU1NTg2MXww&ixlib=rb-4.1.0&q=85";

const FALLBACK_PLANS = [
  { id: "website", name: "Ecommerce Website", price: 1999, period: "year", tagline: "A full online store — cart, payments, SEO." },
  { id: "website_app", name: "Website + Flutter App", price: 5998, period: "year", tagline: "Website + your own app with Play Store approval support.", highlight: true },
  { id: "website_app_ios", name: "iOS + Android + Website", price: 19999, period: "year", tagline: "Website + Android + iOS. You use your own Apple account." },
];

function yearlyPlans(plans) {
  const list = Array.isArray(plans) ? plans : [];
  const yearly = list.filter((p) => {
    const per = String(p?.period || "year").toLowerCase().trim();
    return !["month", "mo", "monthly", "m"].includes(per);
  });
  return yearly.length ? yearly : FALLBACK_PLANS;
}

export default function MerchantSignup() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { refresh } = useMerchantAuth();

  const allowedPlans = ["website", "website_app", "website_app_ios"];
  const planParam = allowedPlans.includes(sp.get("plan")) ? sp.get("plan") : "website";
  const cfOrderId = sp.get("cf_order_id");
  const refParam = (sp.get("ref") || "").trim();
  const themeParam = ["classic", "modern", "trendy", "perfecto", "boxy", "medico", "mixy", "various", "beast", "locally", "quicksto", "bigshow", "furnis", "carparts", "cosmos", "ondemand"].includes(sp.get("theme") || "") ? sp.get("theme") : "";
  const domainParam = (sp.get("domain") || "").trim().toLowerCase();
  const [domainOnly, setDomainOnly] = useState(sp.get("domain_only") === "1");

  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", store_name: "" });
  const [plan, setPlan] = useState(planParam);
  const [pricing, setPricing] = useState(null);
  const [avatars, setAvatars] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!cfOrderId);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [contact, setContact] = useState({ address: "", city: "", state: "", zipcode: "" });
  const [domainQuote, setDomainQuote] = useState(null);
  const [themeQuote, setThemeQuote] = useState(null);

  useEffect(() => {
    api.get("/landing").then(({ data }) => {
      setPricing(data.pricing);
      const raw = Array.isArray(data.brand?.hero_avatars) ? data.brand.hero_avatars : [];
      setAvatars([0, 1, 2, 3].map((i) => raw[i] || ""));
    }).catch(() => setPricing(null));
  }, []);

  useEffect(() => {
    if (domainParam && !form.store_name) {
      const sld = domainParam.split(".")[0] || "";
      if (sld) setForm((f) => ({ ...f, store_name: sld.replace(/-/g, " ") }));
    }
  }, [domainParam]);

  useEffect(() => {
    if (!domainParam) { setDomainQuote(null); return; }
    const planId = domainOnly ? "domain_only" : plan;
    api.post("/domains/quote", { domain: domainParam, plan_id: planId, coupon_code: coupon?.code || null })
      .then(({ data }) => setDomainQuote(data))
      .catch(() => setDomainQuote(null));
  }, [domainParam, domainOnly, plan, coupon]);

  useEffect(() => {
    if ((themeParam !== "carparts" && themeParam !== "ondemand") || domainParam) { setThemeQuote(null); return; }
    api.get("/auth/signup/quote", { params: { plan_id: plan, storefront_theme: themeParam, coupon_code: coupon?.code || undefined } })
      .then(({ data }) => setThemeQuote(data))
      .catch(() => setThemeQuote(null));
  }, [themeParam, plan, coupon, domainParam]);

  useEffect(() => {
    if (refParam) {
      try { sessionStorage.setItem("merastartup_ref", refParam); } catch { /* ignore */ }
    }
  }, [refParam]);

  useEffect(() => {
    if (!cfOrderId) return;
    let alive = true;
    (async () => {
      try {
        const path = cfOrderId.startsWith("dom_") ? `/domains/verify/${cfOrderId}` : `/auth/signup/verify/${cfOrderId}`;
        const { data } = await api.get(path);
        if (!alive) return;
        if (data.active) {
          toast.success(data.buy_store === false || data.plan_id === "domain_only"
            ? `Domain ${data.domain || ""} is yours`
            : `Payment successful — your store is live at yoursaas.com/${data.tenant.slug}`);
          await refresh();
          nav(data.buy_store === false || data.plan_id === "domain_only" ? "/admin/domain" : "/admin");
          return;
        }
        toast.error("We couldn't confirm your payment. If money was deducted it will auto-reflect shortly.");
      } catch (e) {
        if (alive) toast.error(extractErr(e));
      }
      if (alive) {
        setVerifying(false);
        sp.delete("cf_order_id");
        setSp(sp, { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [cfOrderId]);

  const plans = yearlyPlans(pricing?.plans);
  const selectedPlan = plans.find((p) => p.id === plan) || plans[0];
  const usingDomain = Boolean(domainParam);
  const basePrice = usingDomain
    ? Number(domainQuote?.base_amount ?? ((domainOnly ? 0 : Number(selectedPlan?.price || 0))))
    : Number(selectedPlan?.price || 0);
  const finalPrice = usingDomain
    ? Number(domainQuote?.base_amount ?? basePrice)
    : (coupon ? Number(coupon.final_amount) : basePrice);
  const rupee2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const gstAmount = usingDomain
    ? Number(domainQuote?.gst_amount || 0)
    : (themeQuote ? Number(themeQuote.gst_amount || 0) : (finalPrice < 1 ? 0 : Math.round(finalPrice * 0.18 * 100) / 100));
  const payable = usingDomain
    ? Number(domainQuote?.total || 0)
    : (themeQuote ? Number(themeQuote.total || 0) : (finalPrice < 1 ? 0 : Math.round((finalPrice + gstAmount) * 100) / 100));
  const isFree = payable < 1;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const phoneDigits = (form.phone || "").replace(/\D/g, "").slice(-10);

  useEffect(() => {
    if (!form.name.trim() || !emailOk || phoneDigits.length !== 10) return undefined;
    const body = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: phoneDigits,
      plan_id: usingDomain && domainOnly ? "domain_only" : plan,
      store_name: form.store_name.trim(),
    };
    const timer = setTimeout(() => {
      api.post("/auth/signup/intent", body).catch(() => {});
    }, 700);
    return () => clearTimeout(timer);
  }, [form.name, form.email, form.store_name, phoneDigits, emailOk, plan, domainOnly, usingDomain]);

  const choosePlan = (id) => {
    setPlan(id);
    setCoupon(null);
    const next = new URLSearchParams(sp);
    next.set("plan", id);
    if (usingDomain && id) next.delete("domain_only");
    setSp(next, { replace: true });
  };

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/auth/signup/validate-coupon", { code, plan_id: plan, storefront_theme: themeParam || null });
      if (data.valid) {
        setCoupon(data);
        toast.success(`Coupon applied — you save ${rupee(data.discount)}`);
      } else {
        toast.error("Invalid coupon code");
      }
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponInput(""); };

  const checkoutPayload = () => {
    let referral_code = refParam;
    try { referral_code = referral_code || sessionStorage.getItem("merastartup_ref") || null; } catch { /* ignore */ }
    const body = { ...form, preset: "general", plan_id: plan, coupon_code: coupon?.code || null, referral_code };
    if (themeParam) body.storefront_theme = themeParam;
    if (usingDomain) {
      body.domain = domainParam;
      body.plan_id = domainOnly ? "domain_only" : plan;
      body.address = contact.address;
      body.city = contact.city;
      body.state = contact.state;
      body.zipcode = contact.zipcode;
      body.country = "IN";
      if (domainQuote?.domain_price != null) body.quoted_register = domainQuote.domain_price;
      if (domainQuote?.total != null) body.quoted_total = domainQuote.total;
    }
    return body;
  };

  const doCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(usingDomain ? "/domains/checkout" : "/auth/signup/checkout", checkoutPayload());
      if (data.free && data.active) {
        toast.success(usingDomain && (domainOnly || data.buy_store === false) ? "Domain account created" : `Store created with coupon — live at yoursaas.com/${data.tenant.slug}`);
        await refresh();
        nav(usingDomain && (domainOnly || data.buy_store === false) ? "/admin/domain" : "/admin");
        return;
      }
      const cashfree = await loadCashfree({ mode: data.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      toast.error(extractErr(err));
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!emailVerified) {
      setOtpBusy(true);
      try {
        await api.post("/auth/signup/send-otp", { email: form.email });
        setOtpCode("");
        setOtpStep(true);
        toast.success(`We emailed a 6-digit code to ${form.email}`);
      } catch (err) {
        toast.error(extractErr(err));
      } finally {
        setOtpBusy(false);
      }
      return;
    }
    await doCheckout();
  };

  const verifyEmailOtp = async () => {
    setOtpBusy(true);
    try {
      await api.post("/auth/signup/verify-otp", { email: form.email, code: otpCode.trim() });
      setEmailVerified(true);
      setOtpStep(false);
      toast.success("Email verified — taking you to payment…");
      await doCheckout();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setOtpBusy(false);
    }
  };

  const resendSignupOtp = async () => {
    try {
      await api.post("/auth/signup/send-otp", { email: form.email });
      toast.success("New code sent");
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  const field = "ms-signup-input mt-1.5 h-11 rounded-xl";

  const trustCard = (
    <div className="rounded-2xl bg-white border border-black/5 shadow-sm p-4" data-testid="signup-trust-card">
      <div className="text-sm font-bold text-stone-900">Trusted by 10,000+ entrepreneurs</div>
      <div className="mt-2 flex -space-x-2">
        {["A", "B", "C", "D"].map((l, i) => (
          <span key={l} className="w-8 h-8 rounded-full border-2 border-white bg-stone-200 overflow-hidden text-[10px] font-bold flex items-center justify-center">
            {avatars[i] ? <img src={mediaUrl(avatars[i])} alt="" className="w-full h-full object-cover" /> : l}
          </span>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1 text-[#FF6B00]">
        {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
        <span className="ml-1 text-xs font-semibold text-stone-700">4.9/5</span>
      </div>
      <div className="mt-1 text-[11px] text-stone-500">10,000+ stores • UPI • COD • 24/7 Support</div>
    </div>
  );

  if (verifying) {
    return (
      <div className="ms-signup min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF6B00] mx-auto" />
          <p className="mt-4 text-stone-600">Confirming your payment and setting up your store…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ms-signup min-h-screen lg:grid lg:grid-cols-2">
      <SiteHead
        path="/signup"
        title="Create your MeraStartup store"
        description="Sign up for MeraStartup and launch an Indian online store with cart, COD and UPI."
      />
      {otpStep && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-6" data-testid="signup-otp-modal">
          <div className="w-full max-w-sm rounded-2xl border border-stone-200 bg-white p-6 shadow-xl">
            <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center mb-3"><MailCheck className="w-5 h-5 text-[#FF6B00]" /></div>
            <h2 className="ms-serif text-2xl font-bold">Verify your email</h2>
            <p className="text-stone-500 text-sm mt-1">Enter the 6-digit code we sent to <span className="text-stone-800 font-medium">{form.email}</span></p>
            <Input required inputMode="numeric" maxLength={6} autoFocus data-testid="signup-otp-input" placeholder="••••••" className="mt-4 text-center text-2xl tracking-[0.5em] font-mono-nums h-12 rounded-xl" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} />
            <Button type="button" onClick={verifyEmailOtp} disabled={otpBusy || loading || otpCode.length < 6} data-testid="signup-otp-verify" className="w-full h-11 mt-4 bg-[#FF6B00] hover:bg-[#e86000] text-white font-bold rounded-full">
              {otpBusy || loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> Verifying…</>) : "Verify & continue"}
            </Button>
            <div className="flex items-center justify-between text-sm mt-3">
              <button type="button" onClick={() => { setOtpStep(false); setOtpCode(""); }} className="text-stone-500 hover:text-stone-800" data-testid="signup-otp-cancel">Cancel</button>
              <button type="button" onClick={resendSignupOtp} className="text-[#FF6B00] hover:text-[#c45500]" data-testid="signup-otp-resend">Resend code</button>
            </div>
          </div>
        </div>
      )}

      <aside className="relative hidden lg:flex min-h-screen overflow-hidden">
        <img src={LEFT_PHOTO} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF2] via-[#FFFBF2]/75 to-[#FFFBF2]/35" />
        <div className="absolute bottom-0 inset-x-0 h-28 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(255,153,51,0.55) 0%, rgba(255,255,255,0.35) 50%, rgba(19,136,8,0.5) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col p-6 sm:p-10 lg:p-12">
          <Brand variant="light" size="lg" placement="header" />
          <span className="mt-6 inline-flex self-start items-center gap-1.5 rounded-full bg-[#E8F8F0] border border-[#2ED4A6]/40 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-800">
            <Heart className="w-3.5 h-3.5 fill-[#1B7A4A] text-[#1B7A4A]" /> Made for Bharat, designed for you
          </span>
          <h1 className="ms-serif mt-5 text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] font-bold max-w-md">
            <span className="block text-stone-900">Start your Dukaan.</span>
            <span className="block"><span className="text-[#E85D04]">Grow</span> your <span className="text-[#1B7A4A] ms-bharat">Bharat.</span></span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-stone-600 max-w-sm">
            Launch your online store in 60 seconds and start selling to millions of customers.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-stone-800">
            {[
              { icon: Zap, title: "Instant Setup", body: "Your store is live in 60 seconds." },
              { icon: IndianRupee, title: "No Hidden Charges", body: "Transparent pricing. Zero surprises." },
              { icon: ShieldCheck, title: "Built for Bharat", body: "UPI payments, COD, regional support." },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.title} className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-xl bg-white/90 border border-black/5 text-[#1B7A4A] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
                  <span>
                    <span className="font-bold block">{f.title}</span>
                    <span className="text-stone-600 text-[13px]">{f.body}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-auto pt-8 max-w-sm">
            {trustCard}
          </div>
        </div>
      </aside>

      <div className="bg-white lg:bg-transparent flex flex-col lg:overflow-y-auto lg:h-screen">
        <div className="w-full max-w-xl mx-auto p-5 sm:p-8 lg:p-10 flex-1">
          <div className="lg:hidden mb-5">
            <Link to="/" className="inline-block" data-testid="signup-mobile-brand">
              <Brand variant="light" size="lg" placement="header" />
            </Link>
            <div className="mt-5">{trustCard}</div>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900" data-testid="signup-back">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#E8F8F0] text-[#1B7A4A] flex items-center justify-center shrink-0"><Store className="w-5 h-5" /></span>
              <div>
                <h2 className="ms-serif text-3xl font-bold leading-tight">Create your <span className="ms-bharat">store</span></h2>
                <p className="text-stone-500 text-sm mt-1.5 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Payment required — your store is created right after checkout.</p>
              </div>
            </div>
            {themeParam ? (
              <div className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-stone-800 flex items-center gap-2" data-testid="signup-theme-chip">
                <Sparkles className="w-4 h-4 shrink-0 text-[#FF6B00]" />
                {(themeParam === "modern" ? "Modern" : themeParam === "trendy" ? "Trendy" : themeParam === "perfecto" ? "Perfecto" : themeParam === "boxy" ? "Boxy" : themeParam === "medico" ? "Medico" : themeParam === "mixy" ? "Mixy" : themeParam === "various" ? "Various" : themeParam === "beast" ? "Beast" : themeParam === "locally" ? "Locally" : themeParam === "quicksto" ? "QuickSto" : themeParam === "bigshow" ? "BigShow" : themeParam === "furnis" ? "Furnis" : themeParam === "carparts" ? "CarParts" : themeParam === "cosmos" ? "Cosmos" : themeParam === "ondemand" ? "Ondemand" : "Classic")} storefront selected from the demo. We’ll apply it after signup.
              </div>
            ) : null}
            {usingDomain ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-stone-800" data-testid="signup-domain-chip">
                You’re buying <b>{domainParam}</b>
                {domainQuote ? <> — {rupee(domainQuote.domain_price)} first year</> : null}
                {domainQuote?.renew_price != null ? <> · renew {rupee(domainQuote.renew_price)}/yr</> : null}
                {domainQuote?.premium ? " · premium" : ""}
                {domainOnly ? " · domain only" : " · plus your website plan"}
              </div>
            ) : null}
            {usingDomain && domainOnly ? (
              <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4" data-testid="signup-domain-upsell">
                <div className="font-bold text-stone-900">More than 10,000+ people choose to build a website with us</div>
                <p className="text-sm text-stone-600 mt-1">Add website to this package at just {rupee(plans.find((p) => p.id === "website")?.price || 1999)}/year?</p>
                <Button type="button" onClick={() => { setDomainOnly(false); choosePlan("website"); }} data-testid="signup-add-website" className="mt-3 bg-[#FF6B00] hover:bg-[#e86000] text-white rounded-full">
                  Add website
                </Button>
              </div>
            ) : null}

            {(!usingDomain || !domainOnly) ? (
            <div className={`grid grid-cols-1 ${plans.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`}>
              {plans.map((p) => {
                const sel = plan === p.id;
                const popular = p.highlight || p.id === "website_app";
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => choosePlan(p.id)}
                    data-testid={`signup-plan-${p.id}`}
                    className={`relative text-left rounded-2xl border p-4 transition ${sel ? "border-[#FF6B00] bg-orange-50 ring-1 ring-[#FF6B00]/40" : "border-stone-200 bg-white hover:border-stone-300"}`}
                  >
                    {popular ? (
                      <span className="absolute -top-2.5 right-3 text-[10px] font-extrabold uppercase tracking-wide bg-[#FF6B00] text-white rounded-full px-2 py-0.5">Most Popular</span>
                    ) : null}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-stone-900">{p.name}</span>
                      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${sel ? "border-[#FF6B00]" : "border-stone-300"}`}>
                        {sel ? <span className="w-2 h-2 rounded-full bg-[#FF6B00]" /> : null}
                      </span>
                    </div>
                    <div className="mt-1 ms-serif text-2xl font-extrabold text-stone-900">{rupee(p.price)}<span className="text-xs font-normal text-stone-500">/{p.period || "year"}</span></div>
                    <div className="mt-1 text-xs text-stone-500 line-clamp-2">{p.tagline}</div>
                  </button>
                );
              })}
            </div>
            ) : null}

            <div>
              <Label className="text-stone-800 font-semibold">{usingDomain && domainOnly ? "Account name" : "Store name"}</Label>
              <Input required data-testid="signup-store-name" placeholder="E.g. Fresh Fruits" className={field} value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} />
              <div className="text-xs text-stone-500 mt-1.5">{usingDomain ? "We’ll attach this name to your account and storefront." : "We’ll create your store URL automatically. You can connect your own domain later."}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-stone-800 font-semibold">Your name</Label>
                <Input required data-testid="signup-name" placeholder="Enter your name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-stone-800 font-semibold">Email</Label>
                <div className="relative">
                  <Input required type="email" data-testid="signup-email" placeholder="you@email.com" className={`${field} pr-10`} value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setEmailVerified(false); }} />
                  {emailOk ? <CheckCircle2 className="w-4 h-4 text-emerald-500 absolute right-3 top-1/2 -translate-y-1/2 mt-0.5" /> : null}
                </div>
              </div>
              <div>
                <Label className="text-stone-800 font-semibold">Phone number</Label>
                <div className="mt-1.5 flex h-11 rounded-xl border border-stone-200 bg-white overflow-hidden focus-within:border-[#FF6B00]">
                  <span className="px-3 flex items-center gap-1 text-sm text-stone-600 border-r border-stone-200 bg-stone-50 shrink-0">🇮🇳 +91</span>
                  <Input required type="tel" inputMode="numeric" maxLength={15} placeholder="10-digit mobile number" data-testid="signup-phone" className="border-0 shadow-none focus-visible:ring-0 h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="text-xs text-stone-500 mt-1">Required for payment & order updates.</div>
              </div>
              <div>
                <Label className="text-stone-800 font-semibold">Password</Label>
                <div className="relative">
                  <Input required type={showPw ? "text" : "password"} minLength={6} data-testid="signup-password" placeholder="Min. 6 characters" className={`${field} pr-10`} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-stone-400 hover:text-stone-700" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}>
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {usingDomain ? (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="signup-registrant">
                <div className="sm:col-span-2">
                  <Label className="text-stone-800 font-semibold">Registrant address</Label>
                  <Input required data-testid="signup-address" placeholder="Street address" className={field} value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} />
                </div>
                <div>
                  <Label className="text-stone-800 font-semibold">City</Label>
                  <Input required data-testid="signup-city" className={field} value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} />
                </div>
                <div>
                  <Label className="text-stone-800 font-semibold">State</Label>
                  <Input required data-testid="signup-state" className={field} value={contact.state} onChange={(e) => setContact({ ...contact, state: e.target.value })} />
                </div>
                <div>
                  <Label className="text-stone-800 font-semibold">PIN code</Label>
                  <Input required data-testid="signup-zip" className={field} value={contact.zipcode} onChange={(e) => setContact({ ...contact, zipcode: e.target.value })} />
                </div>
              </div>
            ) : null}

            {(!usingDomain || !domainOnly) ? (
            <div>
              <Label className="text-stone-800 font-semibold">Have a coupon code?</Label>
              {coupon ? (
                <div className="mt-1.5 flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                  <span className="text-sm text-emerald-800 flex items-center gap-1.5"><Tag className="w-4 h-4" /> <b>{coupon.code}</b> applied — you save {rupee(coupon.discount)}</span>
                  <button type="button" onClick={removeCoupon} data-testid="signup-coupon-remove" className="text-emerald-700 hover:text-stone-900"><XCircle className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="mt-1.5 relative">
                  <Input data-testid="signup-coupon-input" placeholder="E.g. LAUNCH50" className={`${field} pr-24 uppercase`} value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} />
                  <Button type="button" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()} data-testid="signup-coupon-apply" variant="ghost" className="absolute right-1 top-1.5 h-8 px-3 text-sm font-semibold text-stone-600">
                    {couponBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}
            </div>
            ) : null}

            <div className="rounded-2xl border border-stone-200 bg-[#F7F4EE] p-4 text-sm">
              {usingDomain ? (
                <>
                  <div className="flex items-center justify-between text-stone-600"><span>Domain {domainParam}{domainQuote?.premium ? " (premium)" : ""}</span><span>{rupee(domainQuote?.domain_price || 0)}</span></div>
                  {domainQuote?.renew_price != null ? (
                    <div className="flex items-center justify-between text-stone-500 text-xs mt-1"><span>Renewal from next year</span><span>{rupee(domainQuote.renew_price)}/yr</span></div>
                  ) : null}
                </>
              ) : null}
              {(!usingDomain || !domainOnly) ? (
                themeQuote ? (
                  <div className={`flex items-center justify-between text-stone-600 ${usingDomain ? "mt-1" : ""}`} data-testid="signup-theme-line">
                    <span>{themeParam === "ondemand" ? "Ondemand Theme" : "Car Parts Theme"}</span>
                    <span>{rupee2(themeQuote.plan_amount ?? selectedPlan?.price ?? 0)}</span>
                  </div>
                ) : (
                <div className={`flex items-center justify-between text-stone-600 ${usingDomain ? "mt-1" : ""}`}><span>{selectedPlan?.name}</span><span>{rupee(usingDomain ? (domainQuote?.plan_amount || selectedPlan?.price || 0) : (selectedPlan?.price || 0))}</span></div>
                )
              ) : (
                <div className="flex items-center justify-between text-stone-600 mt-1"><span>Domain only</span><span>—</span></div>
              )}
              {themeQuote && (themeQuote.addons || []).length ? (themeQuote.addons.map((row) => (
                <div key={row.id} className="flex items-center justify-between text-stone-600 mt-1" data-testid="signup-addon-line">
                  <span>{row.name} Addon</span>
                  <span>{rupee2(row.price)}</span>
                </div>
              ))) : themeQuote && Number(themeQuote.addon_list_amount ?? themeQuote.addon_amount ?? 0) > 0 ? (
                <div className="flex items-center justify-between text-stone-600 mt-1" data-testid="signup-addon-line">
                  <span>{themeQuote.addon_name || "Car Parts"} Addon</span>
                  <span>{rupee2(themeQuote.addon_amount)}</span>
                </div>
              ) : null}
              {coupon && (
                <div className="flex items-center justify-between text-emerald-700 mt-1"><span>Coupon {coupon.code}</span><span>−{rupee(coupon.discount)}</span></div>
              )}
              {!isFree && (
                <div className="flex items-center justify-between text-stone-600 mt-1"><span>GST (18%)</span><span data-testid="signup-gst">{rupee2(gstAmount)}</span></div>
              )}
              <div className="flex items-center justify-between text-stone-900 font-bold mt-2 pt-2 border-t border-dashed border-stone-300"><span>Total payable</span><span data-testid="signup-total">{isFree ? "FREE" : rupee2(payable)}</span></div>
              <div className="mt-2 text-[11px] text-stone-500">Incl. 18% GST · GSTIN 09FDFPB7488N1ZR · Grocode</div>
            </div>

            <Button type="submit" disabled={loading || otpBusy} data-testid="signup-submit" className="w-full h-12 bg-[#FF6B00] hover:bg-[#e86000] text-white font-bold rounded-full">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> {isFree ? "Creating store…" : "Redirecting to payment…"}</>)
                : otpBusy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> Sending code…</>)
                : !emailVerified ? (<><Lock className="w-4 h-4 mr-2" />Verify email & continue</>)
                : (isFree ? <>Create my store for FREE</> : <>Pay {rupee2(payable)} & create store</>)}
            </Button>
            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-500">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Secure payment by Cashfree
            </div>
            <div className="text-sm text-stone-500 text-center pb-4">
              Already have a store? <Link to="/login" className="text-[#FF6B00] font-semibold hover:text-[#c45500]" data-testid="signup-to-login">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
